import React, { useState, useEffect } from "react";
import { IconButton, Box, Typography, Divider } from "@mui/material";
import AppDrawer from "../../../AppComponents/AppDrawer";
import { AppMaterialIcons } from "../../../AppComponents/AppMaterialIcons";
import { useNotification } from "../../../Hooks/useNotification/useNotification";
import AppPaginationComponent from "../../../AppComponents/Pagination/Pagination";
import { TableWrapper } from "../../../TableStyle";
import { theme } from "../../../Customization/Theme";
import styled from "styled-components";
import { ApiLog, ApiLogFilters, ApiLogsService } from "../../../Store/Slices/APILibrary/apiLogs.service";
import ApiLogDetailDrawer from "./ApiLogDetailDrawer";

interface Props {
  open: boolean;
  onClose: () => void;
  apiId?: string;
  apiName?: string;
}

const ApiLogsDrawer: React.FC<Props> = ({ open, onClose, apiId, apiName }) => {
  const { showAlert } = useNotification();
  const [logs, setLogs] = useState<ApiLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<any>({
    currentPage: 1,
    perPage: 10,
    totalPages: 0,
    totalRecords: 0,
  });
  const [selectedLog, setSelectedLog] = useState<ApiLog | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);

  const [filters, setFilters] = useState<ApiLogFilters>({
    page_number: 0,
    page_size: 10,
    status: null,
    search: "",
    api_id: apiId || "",
    status_code: null,
  });

  const [searchInput, setSearchInput] = useState<string>("");

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
  }, [open, filters]);

  // Debounce search input to avoid calling API on every keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchInput,
        page_number: 0,
      }));
    }, 400);

    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await ApiLogsService.fetchApiLogs({
        ...filters,
        api_id: apiId || "",
      });
      if (response.data && response.data.status) {
        setLogs(response.data.data || []);
        setPagination(
          response.data.pagination || {
            currentPage: 1,
            perPage: 10,
            totalPages: 0,
            totalRecords: 0,
          }
        );
      }
    } catch (error: any) {
      showAlert(
        error?.response?.data?.error || "Failed to fetch API logs",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof ApiLogFilters, value: any) => {
    setFilters({
      ...filters,
      [field]: value,
      page_number: 0, // Reset to first page when filters change
    });
  };

  const handlePageChange = (event: any, value: any) => {
    setFilters({
      ...filters,
      page_number: value - 1, // Convert from 1-indexed to 0-indexed
    });
  };

  const handleChangeRowsPerPage = (value: any) => {
    setFilters({
      ...filters,
      page_size: value.value,
      page_number: 0,
    });
  };

  const handleLogClick = (log: ApiLog) => {
    setSelectedLog(log);
    setIsDetailDrawerOpen(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <AppDrawer
        sx={{
          "& .MuiPaper-root": { width: "calc(100vw - 270px)" },
        }}
        open={open}
        onClose={onClose}
      >
        <LogsDrawerWrapper>
          <div className="drawer__header">
            <div className="header__title">
              API Logs {apiName ? `- ${apiName}` : ""}
            </div>
            <IconButton onClick={onClose}>
              <AppMaterialIcons iconName="Close" />
            </IconButton>
          </div>

          <div className="drawer__filters">
            <div className="search__input">
              <input
                type="text"
                 placeholder="Search by url, action & response..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <div className="filter__row">
              <div className="filter__select">
                <select
                  value={filters.status || ""}
                  onChange={(e) =>
                    handleFilterChange("status", e.target.value || null)
                  }
                >
                  <option value="">All Status</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>
          </div>

          <div className="drawer__body">
            {loading ? (
              <div className="no__data">Loading...</div>
            ) : logs.length === 0 ? (
              <div className="no__data">No logs found</div>
            ) : (
              <TableWrapper>
                <div
                  style={{ overflow: "auto", height: "calc(100vh - 280px)" }}
                >
                  <Box className="table-header">
                    <Box className="table-row">
                      {["URL", "Action", "Type", "Status Code"].map(
                        (header, index) => (
                          <Box
                            className={`col-head ${header
                              .toLowerCase()
                              .replaceAll(" ", "_")}`}
                            style={{
                              minWidth: `${
                                header === "URL"
                                  ? 200
                                  : header === "Request"
                                  ? 150
                                  : 120
                              }px`,
                              border: "none",
                            }}
                            key={index}
                          >
                            <Typography sx={{ fontWeight: 500 }}>
                              {header}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                    <Divider />
                  </Box>

                  <Box className="table-body">
                    {logs.map((log, index) => (
                      <Box
                        className="table-row"
                        key={`${log.api_id}-${index}`}
                        onClick={() => handleLogClick(log)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "#f5f5f5" },
                        }}
                      >
                        <Box className="col-body" style={{ minWidth: "200px" }}>
                          <Typography
                            className="table-text"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textTransform:"lowercase"
                            }}
                          >
                            {log.url || "-"}
                          </Typography>
                        </Box>
                        <Box className="col-body" style={{ minWidth: "200px" }}>
                          <Typography className="table-text">
                            {log.action || "-"}
                          </Typography>
                        </Box>
                        <Box className="col-body" style={{ minWidth: "120px" }}>
                          <Typography className="table-text">
                            {log.type || "-"}
                          </Typography>
                        </Box>
                        <Box className="col-body" style={{ minWidth: "120px" }}>
                          <Typography
                            className="table-text"
                            sx={{
                              color:
                                log.status_code >= 200 && log.status_code < 300
                                  ? "green"
                                  : "red",
                              fontWeight: 500,
                            }}
                          >
                            {log.status_code || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </div>
              </TableWrapper>
            )}
          </div>

          {!loading && logs.length > 0 && (
            <div className="pagination__wrapper">
              <AppPaginationComponent
                totalCount={pagination?.totalRecords}
                handlePageChange={handlePageChange}
                currentPage={pagination?.currentPage - 1}
                totalPages={pagination?.totalPages}
                rowsPerPage={filters.page_size}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                title="Logs"
              />
            </div>
          )}
        </LogsDrawerWrapper>
      </AppDrawer>

      {selectedLog && (
        <ApiLogDetailDrawer
          open={isDetailDrawerOpen}
          onClose={() => {
            setIsDetailDrawerOpen(false);
            setSelectedLog(null);
          }}
          log={selectedLog}
        />
      )}
    </>
  );
};

export default ApiLogsDrawer;


export const LogsDrawerWrapper = styled.div`
  && {
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;

    .drawer__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 22px;
      height: 63px;
      border-bottom: 1px solid ${theme.palette.default.BorderColorGrey};
      
      .header__title {
        font-size: 16px;
        font-weight: 500;
        color: ${theme.palette.default.black};
      }
    }

    .drawer__filters {
      padding: 16px 22px;
      border-bottom: 1px solid ${theme.palette.default.BorderColorGrey};
      display: flex;
      flex-direction: column;
      gap: 12px;

      .filter__row {
        display: flex;
        gap: 8px;
        align-items: center;
      }

      .search__input {
        width: 100%;
        
        input {
          width: 100%;
          height: 36px;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 14px;
          border: 1px solid ${theme.palette.default.BorderColorGrey};
          outline: none;

          &:focus {
            border-color: ${theme.palette.primary.main};
          }

          &::placeholder {
            color: ${theme.palette.default.darkGrey};
          }
        }
      }

      .filter__select {
        flex: 1;
        
        select {
          width: 100%;
          height: 36px;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 14px;
          border: 1px solid ${theme.palette.default.BorderColorGrey};
          outline: none;
          background-color: ${theme.palette.default.white};
          cursor: pointer;

          &:focus {
            border-color: ${theme.palette.primary.main};
          }
        }
      }

      .date__input {
        flex: 1;
        
        input[type="date"] {
          width: 100%;
          height: 36px;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 14px;
          border: 1px solid ${theme.palette.default.BorderColorGrey};
          outline: none;
          background-color: ${theme.palette.default.white};
          cursor: pointer;

          &:focus {
            border-color: ${theme.palette.primary.main};
          }
        }
      }
    }

    .drawer__body {
      flex: 1;
      overflow-y: auto;
      padding: 16px 22px;
      padding-bottom: 0;
    }

    .log__item {
      border: 1px solid ${theme.palette.default.BorderColorGrey};
      border-radius: 4px;
      padding: 12px;
      margin-bottom: 12px;
      cursor: pointer;
      transition: all 0.2s;
      background-color: ${theme.palette.default.white};

      &:hover {
        box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
        border-color: ${theme.palette.primary.main};
      }

      .log__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;

        .log__status {
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.5px;

          &.success {
            background-color: ${theme.palette.default.teaGreen};
            color: ${theme.palette.default.success};
            border: 1px solid ${theme.palette.default.success};
          }

          &.failed {
            background-color: ${theme.palette.default.palePink};
            color: ${theme.palette.default.error};
            border: 1px solid ${theme.palette.default.error};
          }
        }

        .log__time {
          font-size: 12px;
          color: ${theme.palette.default.darkGrey};
          font-weight: 500;
        }
      }

      .log__details {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .log__text {
          font-size: 13px;
          color: ${theme.palette.default.black};
          line-height: 1.4;
          
          .label {
            font-weight: 600;
            color: ${theme.palette.default.darkGrey};
            margin-right: 4px;
          }
        }
      }
    }

    .no__data {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: ${theme.palette.default.darkGrey};
      font-size: 14px;
    }

    .pagination__wrapper {
      padding: 12px 22px;
      border-top: 1px solid ${theme.palette.default.BorderColorGrey};
      background-color: ${theme.palette.default.white};
    }
  }
`;